import * as React from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { TreeMenuView, TreeMenuViewProps } from './TreeMenu.view';
import {
  CoreImportFolderFieldsFragment,
  ImportSourceFieldsFragment,
  ImportSourceType,
  Network,
  SocialOAuthProvider
} from '../../../../models/graphql';
import { IconsEnum } from '../../../../constants/icons';
import { showToast } from '../../../../helpers/showToast';
import { TreeMenuModalTypeEnums } from '../../../../constants/find-content';
import { TreeMenuItemType, TreeType } from '../../../../models/find-content';
import { remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { TreeMenuContext } from './TreeMenuProvider';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';

export type TreeMenuControllerProps = {
  updateFolderSortOrder: (folderId: string, order: number) => Promise<void>;
  updateSourceSortOrder: (importSourceId: string, importFolderId: string | null, order: number) => Promise<void>;
  reFetch: CallableFunction;
};

export const mapInputSourceTypeToProvider = (type: ImportSourceType): SocialOAuthProvider | 'RSS' => {
  switch (type) {
    case ImportSourceType.FACEBOOK:
      return SocialOAuthProvider.FACEBOOK;
    case ImportSourceType.TWITTER:
      return SocialOAuthProvider.TWITTER;
    case ImportSourceType.LINKEDIN:
      return SocialOAuthProvider.LINKEDIN;
    case ImportSourceType.INSTAGRAM:
      return SocialOAuthProvider.INSTAGRAM;
    case ImportSourceType.PINTEREST:
      return SocialOAuthProvider.PINTEREST;
    case ImportSourceType.RSS:
      return 'RSS';
  }
};

export const mapInputSourceTypeToNetwork = (type: ImportSourceType): Network => {
  switch (type) {
    case ImportSourceType.FACEBOOK:
      return Network.FACEBOOK;
    case ImportSourceType.TWITTER:
      return Network.TWITTER;
    case ImportSourceType.LINKEDIN:
      return Network.LINKEDIN;
    case ImportSourceType.INSTAGRAM:
      return Network.INSTAGRAM;
    case ImportSourceType.PINTEREST:
      return Network.PINTEREST;
    case ImportSourceType.RSS:
      return Network.GENERIC_PROFILE;
  }
};

// When sources are at the root, they have a null parent folder
const mapSourcesByParentFolder = (
  parentId: string | null,
  sources: ImportSourceFieldsFragment[],
  action: (
    type: TreeMenuModalTypeEnums | undefined,
    item: ImportSourceFieldsFragment | CoreImportFolderFieldsFragment | undefined
  ) => void
): TreeMenuItemType[] => {
  const subItemSources =
    parentId !== null ? sources.filter(s => s.folder?.id === parentId) : sources.filter(s => s.folder === null);

  return subItemSources.map(s => {
    return {
      id: `source-${s.id}`,
      type: DraggableTypesObject.MENU_TREE_SOURCE,
      label: s.name || '',
      icon: undefined,
      provider: mapInputSourceTypeToProvider(s.type),
      network: mapInputSourceTypeToNetwork(s.type),
      hasMenu: true,
      menuItems: [
        {
          label: 'Rename',
          action: () => {
            action(TreeMenuModalTypeEnums.RENAME_SOURCE, s);
          }
        },
        {
          label: 'Delete',
          action: () => {
            action(TreeMenuModalTypeEnums.DELETE_SOURCE, s);
          }
        }
      ]
    };
  });
};

const treeMenuItemToTreeType = (treeItem: TreeMenuItemType, index: string, parent?: string): TreeType => {
  return {
    id: index,
    parent: parent || '0',
    droppable: treeItem.type === DraggableTypesObject.MENU_TREE_FOLDER,
    text: treeItem.label,
    data: treeItem
  };
};

const adjustedPosition = (position: number, folderCount: number): number => {
  const adjustRootPosition = position - folderCount;

  return adjustRootPosition < 1 ? 1 : adjustRootPosition;
};

export const TreeMenuController = (props: TreeMenuControllerProps): JSX.Element => {
  const { updateFolderSortOrder, updateSourceSortOrder } = props;
  const { sources, folders } = useContext(TreeMenuContext);
  const { modalType, selectedMenuItem, setActionCondition } = useContext(TreeMenuContext);

  const mapFolders = useMemo(
    () =>
      folders.map<TreeMenuItemType>(t => ({
        id: `folder-${t.id}`,
        type: DraggableTypesObject.MENU_TREE_FOLDER,
        label: t.name || '',
        hasMenu: true,
        icon: IconsEnum.folder,
        menuItems: [
          {
            label: 'Rename',
            action: () => setActionCondition(TreeMenuModalTypeEnums.RENAME_FOLDER, t)
          },
          {
            label: 'Delete',
            action: () => setActionCondition(TreeMenuModalTypeEnums.DELETE_FOLDER, t)
          }
        ],
        subItems: mapSourcesByParentFolder(t.id, sources, setActionCondition)
      })),
    [folders, setActionCondition, sources]
  );

  const updateMenuItem = useCallback(
    (itemId: string, parentId: string, position: number, isSourceType?: boolean) => {
      const { 1: id } = itemId.split('-');
      const { 1: folderId } = parentId === '0' ? { 1: null } : parentId.split('-');

      if (isSourceType) {
        const correctedPosition = folderId === null ? adjustedPosition(position, folders.length) : position;
        updateSourceSortOrder(id, folderId, correctedPosition).catch(error => {
          const mappedError = remapApolloErrors(error);
          showToast({
            summary: 'Error moving your saved source',
            detail: mappedError,
            severity: 'error'
          });
        });
      } else {
        updateFolderSortOrder(id, position).catch(error => {
          const mappedError = remapApolloErrors(error);
          showToast({
            summary: 'Error moving your folder',
            detail: mappedError,
            severity: 'error'
          });
        });
      }
    },
    [folders, updateFolderSortOrder, updateSourceSortOrder]
  );

  const treeMenuItems: TreeType[] = useMemo(() => {
    const data = mapFolders.concat(mapSourcesByParentFolder(null, sources, setActionCondition));
    const menuItems: TreeType[] = [];

    data.forEach(menuItem => {
      if (menuItem?.id) {
        menuItems.push(treeMenuItemToTreeType(menuItem, menuItem.id));

        if (menuItem.subItems) {
          menuItem.subItems.forEach(subItem => {
            menuItems.push(treeMenuItemToTreeType(subItem, subItem.id, menuItem.id));
          });
        }
      }
    });

    return menuItems;
  }, [mapFolders, setActionCondition, sources]);

  const viewProps: TreeMenuViewProps = {
    ...props,
    treeMenuItems,
    updateMenuItem,
    showModalType: modalType,
    setActionCondition,
    selectedMenuItem
  };

  return <TreeMenuView {...viewProps} />;
};
